import React, { useState } from 'react';
import { 
  Card, 
  Button, 
  Upload, 
  Select, 
  Alert, 
  Spin, 
  Typography, 
  Divider, 
  List,
  Space,
  message,
  Modal,
  Table
} from 'antd';
import { 
  DownloadOutlined, 
  UploadOutlined, 
  DatabaseOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import api from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const DataManagement = () => {
  const [selectedTable, setSelectedTable] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [duplicateAction, setDuplicateAction] = useState('skip'); // 'skip', 'update', 'error'

  const tables = [
    { key: 'tipo-unidades', label: 'Tipos de Unidade' },
    { key: 'tipo-contratos', label: 'Tipos de Contrato' },
    { key: 'unidades', label: 'Unidades (Escolas)' },
    { key: 'modalidades', label: 'Modalidades' },
    { key: 'equipe', label: 'Equipe (Professores)' },
    { key: 'turmas', label: 'Turmas' },
    { key: 'alunos', label: 'Alunos' },
    { key: 'matriculas', label: 'Matrículas' }
  ];

  const handleExport = async (tableKey) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/export-import/export/${tableKey}`, {
        responseType: 'blob'
      });
      
      // Criar link para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${tableKey}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      message.success(`${tableKey}.xlsx baixado com sucesso!`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail))
        : error.message;
      message.error('Erro ao exportar dados: ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleExportAll = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/export-import/export/all', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `backup_completo_${timestamp}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      message.success('Backup completo baixado com sucesso!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail))
        : error.message;
      message.error('Erro ao exportar backup: ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!selectedTable || !uploadFile) {
      message.warning('Selecione a tabela e o arquivo antes de validar');
      return;
    }

    setLoading(true);
    setValidationResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await api.post(`/api/export-import/validate/${selectedTable}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setValidationResult(response.data);
      
      if (response.data.valid) {
        const tableLabel = tables.find(t => t.key === selectedTable)?.label || selectedTable;
        const hasDuplicates = response.data.duplicate_count && response.data.duplicate_count > 0;
        
        // Modal com preview dos dados
        Modal.success({
          title: hasDuplicates ? '⚠️ Dados Válidos - Duplicados Encontrados' : '✅ Dados Válidos!',
          width: 800,
          content: (
            <div>
              <p><strong>Tabela:</strong> {tableLabel}</p>
              <p><strong>Total de registros:</strong> {response.data.total_records || response.data.total_rows}</p>
              <p><strong>Novos:</strong> {response.data.new_count || 0}</p>
              {hasDuplicates && (
                <p><strong style={{ color: '#ff9800' }}>Duplicados:</strong> {response.data.duplicate_count}</p>
              )}
              
              {hasDuplicates && response.data.duplicates && (
                <Alert 
                  type="warning" 
                  message={`${response.data.duplicate_count} registros duplicados encontrados`}
                  description={
                    <div>
                      <p>Os seguintes registros já existem no banco de dados:</p>
                      <ul style={{ marginTop: 8, maxHeight: 150, overflow: 'auto' }}>
                        {response.data.duplicates.slice(0, 10).map((dup, i) => (
                          <li key={i}>
                            <strong>Linha {dup.linha}:</strong> {dup.nome || dup.valor}
                          </li>
                        ))}
                        {response.data.duplicates.length > 10 && (
                          <li>...e mais {response.data.duplicates.length - 10} duplicados</li>
                        )}
                      </ul>
                      <p style={{ marginTop: 8 }}>
                        <strong>Na importação, você poderá escolher:</strong><br/>
                        • Atualizar os registros existentes<br/>
                        • Ignorar os duplicados<br/>
                        • Bloquear a importação se houver duplicados
                      </p>
                    </div>
                  }
                  style={{ marginTop: 12, marginBottom: 12 }}
                />
              )}
              
              {response.data.warnings && response.data.warnings.length > 0 && (
                <Alert 
                  type="info" 
                  message={`${response.data.warnings.length} avisos encontrados`}
                  description={
                    <ul style={{ marginTop: 8, maxHeight: 150, overflow: 'auto' }}>
                      {response.data.warnings.map((warn, i) => (
                        <li key={i}>{warn}</li>
                      ))}
                    </ul>
                  }
                  style={{ marginTop: 12, marginBottom: 12 }}
                />
              )}
              
              {response.data.preview && response.data.preview.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <p><strong>Preview (primeiras 5 linhas):</strong></p>
                  <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid #d9d9d9', borderRadius: 4, padding: 8 }}>
                    <pre style={{ fontSize: 11, margin: 0 }}>
                      {JSON.stringify(response.data.preview, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
              
              <Alert 
                type="info" 
                message={hasDuplicates 
                  ? "Você poderá escolher como tratar os duplicados na próxima etapa." 
                  : "Pronto para importar! Clique no botão 'Importar' para continuar."}
                style={{ marginTop: 16 }}
              />
            </div>
          )
        });
        
        message.success('Validação concluída com sucesso!');
      } else {
        message.error(`${response.data.error_count || response.data.errors?.length || 0} erros encontrados`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail))
        : error.message;
      message.error('Erro ao validar: ' + errorMsg);
      
      setValidationResult({
        valid: false,
        errors: [errorMsg],
        error_count: 1
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!selectedTable || !uploadFile) {
      message.warning('Selecione a tabela e o arquivo antes de importar');
      return;
    }

    // Se ainda não validou, exige validação primeiro
    if (!validationResult) {
      message.warning('Por favor, valide os dados antes de importar!');
      return;
    }

    // Se validou mas tem erros, bloqueia
    if (validationResult && !validationResult.valid) {
      Modal.error({
        title: '❌ Dados Inválidos',
        content: 'Corrija os erros de validação antes de importar.',
      });
      return;
    }

    const tableLabel = tables.find(t => t.key === selectedTable)?.label || selectedTable;
    const recordCount = validationResult?.total_records || validationResult?.total_rows || '?';
    const hasDuplicates = validationResult?.duplicate_count && validationResult.duplicate_count > 0;
    
    // Se houver duplicados, mostrar modal de escolha
    if (hasDuplicates) {
      const modalInstance = Modal.confirm({
        title: '⚠️ Como tratar os registros duplicados?',
        width: 700,
        icon: <ExclamationCircleOutlined style={{ color: '#ff9800' }} />,
        content: (
          <div>
            <Alert
              type="warning"
              message={`${validationResult.duplicate_count} registros duplicados encontrados`}
              description={
                <div>
                  <p><strong>Novos:</strong> {validationResult.new_count || 0} registros</p>
                  <p><strong>Duplicados:</strong> {validationResult.duplicate_count} registros</p>
                  
                  {validationResult.duplicates && validationResult.duplicates.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <p><strong>Exemplos de duplicados:</strong></p>
                      <ul style={{ maxHeight: 150, overflow: 'auto', fontSize: 12 }}>
                        {validationResult.duplicates.slice(0, 5).map((dup, i) => (
                          <li key={i}>Linha {dup.linha}: {dup.nome || dup.valor}</li>
                        ))}
                        {validationResult.duplicates.length > 5 && (
                          <li>...e mais {validationResult.duplicates.length - 5}</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              }
              style={{ marginTop: 12 }}
            />
            
            <div style={{ marginTop: 16 }}>
              <p><strong>Escolha uma ação:</strong></p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Button 
                  type="primary" 
                  onClick={() => {
                    modalInstance.destroy();
                    performImport('update');
                  }}
                  block
                >
                  🔄 Atualizar Registros Existentes
                </Button>
                <Button 
                  onClick={() => {
                    modalInstance.destroy();
                    performImport('skip');
                  }}
                  block
                >
                  ⏭️ Ignorar Duplicados (Importar Apenas Novos)
                </Button>
                <Button 
                  danger
                  onClick={() => {
                    modalInstance.destroy();
                  }}
                  block
                >
                  ❌ Cancelar Importação
                </Button>
              </div>
            </div>
          </div>
        ),
        footer: null,
        okButtonProps: { style: { display: 'none' } },
        cancelButtonProps: { style: { display: 'none' } }
      });
      
      return;
    }

    // Se não houver duplicados, confirmar importação normal
    Modal.confirm({
      title: '📊 Confirmar Importação',
      width: 700,
      icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
      content: (
        <div>
          <Alert
            type="info"
            message="Esta ação irá inserir os dados no banco"
            description={
              <div>
                <p><strong>{recordCount} registros</strong> serão importados para <strong>{tableLabel}</strong></p>
                
                {validationResult?.warnings && validationResult.warnings.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <p><strong>⚠️ Avisos ({validationResult.warnings.length}):</strong></p>
                    <ul style={{ maxHeight: 100, overflow: 'auto', fontSize: 12 }}>
                      {validationResult.warnings.slice(0, 5).map((warn, i) => (
                        <li key={i}>{warn}</li>
                      ))}
                      {validationResult.warnings.length > 5 && (
                        <li>...e mais {validationResult.warnings.length - 5} avisos</li>
                      )}
                    </ul>
                  </div>
                )}
                
                <p style={{ marginTop: 12, fontWeight: 'bold' }}>
                  Deseja continuar?
                </p>
              </div>
            }
            style={{ marginTop: 16 }}
          />
        </div>
      ),
      okText: 'Sim, Importar Agora',
      okType: 'primary',
      cancelText: 'Cancelar',
      onOk: async () => {
        await performImport('skip');
      }
    });
  };
  
  const performImport = async (action = 'skip') => {
    setLoading(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await api.post(
        `/api/export-import/import/${selectedTable}?duplicate_action=${action}`, 
        formData, 
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setImportResult(response.data);
      
      if (response.data.success) {
        const inserted = response.data.inserted || 0;
        const updated = response.data.updated || 0;
        const skipped = response.data.skipped || 0;
        
        Modal.success({
          title: '✅ Importação Concluída',
          content: (
            <div>
              <p>{response.data.message}</p>
              <div style={{ marginTop: 12 }}>
                {inserted > 0 && <p><strong>✅ Inseridos:</strong> {inserted}</p>}
                {updated > 0 && <p><strong>🔄 Atualizados:</strong> {updated}</p>}
                {skipped > 0 && <p><strong>⏭️ Ignorados:</strong> {skipped}</p>}
              </div>
            </div>
          )
        });
        
        message.success(response.data.message);
        
        // Limpa formulário
        setUploadFile(null);
        setSelectedTable('');
        setValidationResult(null);
      } else {
        Modal.error({
          title: '❌ Importação Falhou',
          content: (
            <div>
              <p>{response.data.message}</p>
              {response.data.errors && response.data.errors.length > 0 && (
                <div style={{ maxHeight: 300, overflow: 'auto', marginTop: 12 }}>
                  <ul>
                    {response.data.errors.map((err, i) => (
                      <li key={i} style={{ color: 'red' }}>
                        {typeof err === 'string' ? err : JSON.stringify(err)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )
        });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail))
        : error.message;
      
      Modal.error({
        title: '❌ Erro na Importação',
        content: errorMsg
      });
      
      message.error('Erro ao importar: ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Mapeamento de nomes de arquivo para tabelas
  const fileNameToTable = {
    'tipo_unidades': 'tipo-unidades',
    'tipo-unidades': 'tipo-unidades',
    'tipo_contratos': 'tipo-contratos',
    'tipo-contratos': 'tipo-contratos',
    'unidades': 'unidades',
    'modalidades': 'modalidades',
    'equipe': 'equipe',
    'turmas': 'turmas',
    'alunos': 'alunos',
    'matriculas': 'matriculas'
  };

  const detectTableFromFileName = (fileName) => {
    // Remove extensão e normaliza
    const baseName = fileName.toLowerCase()
      .replace(/\.(xlsx|xls)$/i, '')
      .trim();
    
    return fileNameToTable[baseName] || null;
  };

  const uploadProps = {
    beforeUpload: (file) => {
      console.log('Upload iniciado:', file.name, file.type, file.size);
      
      // Validação 1: Tipo de arquivo
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                      file.type === 'application/vnd.ms-excel' ||
                      file.name.match(/\.(xlsx|xls)$/i);
      
      if (!isExcel) {
        message.error('Você só pode fazer upload de arquivos Excel (.xlsx ou .xls)!');
        return false;
      }
      
      // Validação 2: Tamanho (máx 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        message.error('Arquivo muito grande! Tamanho máximo: 10MB');
        return false;
      }
      
      // Detecção automática da tabela
      const detected = detectTableFromFileName(file.name);
      console.log('Tabela detectada:', detected);
      
      if (detected) {
        setSelectedTable(detected);
        const tableLabel = tables.find(t => t.key === detected)?.label || detected;
        message.success(
          `✅ Arquivo aceito! Tabela: ${tableLabel}`,
          3
        );
      } else {
        message.warning(
          '⚠️ Não foi possível detectar a tabela. Selecione manualmente no dropdown acima.',
          5
        );
      }
      
      // Definir arquivo e limpar resultados anteriores
      setUploadFile(file);
      setValidationResult(null);
      setImportResult(null);
      
      console.log('Estado atualizado - uploadFile definido');
      
      return false; // Impede upload automático
    },
    onRemove: () => {
      console.log('Arquivo removido');
      setUploadFile(null);
      setSelectedTable('');
      setValidationResult(null);
      setImportResult(null);
    },
    fileList: uploadFile ? [uploadFile] : [],
    maxCount: 1,
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <DatabaseOutlined /> Gestão de Dados
      </Title>
      <Paragraph>
        Exporte os dados atuais para Excel, preencha com seus dados reais e importe de volta.
      </Paragraph>

      {/* Seção de Exportação */}
      <Card 
        title={<><DownloadOutlined /> Exportar Dados</>}
        style={{ marginBottom: 24 }}
      >
        <Paragraph>
          Baixe as tabelas em Excel para ver o formato correto e preencher com seus dados.
        </Paragraph>
        
        <List
          grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 4 }}
          dataSource={tables}
          renderItem={table => (
            <List.Item>
              <Card 
                size="small"
                hoverable
                onClick={() => handleExport(table.key)}
                style={{ cursor: 'pointer' }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <FileExcelOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                  <Text strong>{table.label}</Text>
                  <Button 
                    type="primary" 
                    size="small" 
                    icon={<DownloadOutlined />}
                    block
                  >
                    Baixar
                  </Button>
                </Space>
              </Card>
            </List.Item>
          )}
        />

        <Divider />
        
        <Button 
          type="primary" 
          size="large" 
          icon={<DatabaseOutlined />}
          onClick={handleExportAll}
          loading={loading}
          block
        >
          Baixar Todas as Tabelas em ZIP
        </Button>
      </Card>

      {/* Seção de Importação */}
      <Card 
        title={<><UploadOutlined /> Importar Dados</>}
      >
        <Paragraph>
          Selecione a tabela e faça upload do arquivo Excel preenchido. 
          <Text type="warning"> Sempre valide os dados antes de importar!</Text>
        </Paragraph>

        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>1. Selecione a tabela:</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Escolha a tabela... (ou será detectada automaticamente)"
              value={selectedTable}
              onChange={setSelectedTable}
              size="large"
              allowClear
            >
              {tables.map(t => (
                <Option key={t.key} value={t.key}>{t.label}</Option>
              ))}
            </Select>
            {selectedTable && (
              <Alert
                type="info"
                message={`Tabela selecionada: ${tables.find(t => t.key === selectedTable)?.label}`}
                style={{ marginTop: 8 }}
                showIcon
              />
            )}
          </div>

          <div>
            <Text strong>2. Faça upload do arquivo Excel:</Text>
            <Upload.Dragger 
              {...uploadProps}
              style={{ marginTop: 8 }}
            >
              <p className="ant-upload-drag-icon">
                <FileExcelOutlined />
              </p>
              <p className="ant-upload-text">
                Clique ou arraste o arquivo Excel aqui
              </p>
              <p className="ant-upload-hint">
                Apenas arquivos .xlsx ou .xls
              </p>
            </Upload.Dragger>
          </div>

          {/* Status Visual */}
          {(uploadFile || selectedTable) && (
            <Alert
              type="info"
              showIcon
              message="Status da Importação"
              description={
                <div>
                  <p>
                    <strong>Arquivo:</strong> {uploadFile ? `✅ ${uploadFile.name}` : '❌ Nenhum arquivo selecionado'}
                  </p>
                  <p>
                    <strong>Tabela:</strong> {selectedTable ? `✅ ${tables.find(t => t.key === selectedTable)?.label}` : '❌ Não selecionada'}
                  </p>
                  {uploadFile && selectedTable && (
                    <p style={{ color: '#52c41a', fontWeight: 'bold', marginTop: 8 }}>
                      ✅ Pronto para validar! Clique no botão "Validar Dados" abaixo.
                    </p>
                  )}
                </div>
              }
            />
          )}

          {/* Botões de Ação */}
          <Space style={{ width: '100%' }} size="middle">
            <Button
              type="default"
              icon={<CheckCircleOutlined />}
              onClick={handleValidate}
              disabled={!selectedTable || !uploadFile}
              loading={loading}
              size="large"
              style={{ flex: 1 }}
            >
              🔍 Validar Dados
            </Button>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={handleImport}
              disabled={!selectedTable || !uploadFile || (validationResult && !validationResult.valid)}
              loading={loading}
              size="large"
              style={{ flex: 1 }}
            >
              ✅ Importar
            </Button>
          </Space>

          {/* Resultado da Validação */}
          {validationResult && (
            <Alert
              type={validationResult.valid ? 'success' : 'error'}
              message={validationResult.valid ? 'Validação Concluída' : 'Erros Encontrados'}
              description={
                <div>
                  {validationResult.valid ? (
                    <div>
                      <p><strong>Total de linhas:</strong> {validationResult.total_rows}</p>
                      <p>Todos os dados estão válidos e prontos para importação!</p>
                    </div>
                  ) : (
                    <div>
                      <p><strong>Total de linhas:</strong> {validationResult.total_rows}</p>
                      <p><strong>Erros encontrados:</strong> {validationResult.error_count}</p>
                      <Divider />
                      <div style={{ maxHeight: 300, overflow: 'auto' }}>
                        <ul>
                          {validationResult.errors.map((error, idx) => (
                            <li key={idx}>
                              <Text type="danger">
                                {typeof error === 'string' ? error : JSON.stringify(error)}
                              </Text>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              }
              showIcon
            />
          )}

          {/* Resultado da Importação */}
          {importResult && (
            <Alert
              type={importResult.success ? 'success' : 'error'}
              message={importResult.success ? 'Importação Concluída' : 'Importação Falhou'}
              description={
                <div>
                  {importResult.success ? (
                    <p>{importResult.message}</p>
                  ) : (
                    <div>
                      <p><strong>Registros importados:</strong> {importResult.imported}</p>
                      <Divider />
                      <div style={{ maxHeight: 300, overflow: 'auto' }}>
                        <ul>
                          {importResult.errors.map((error, idx) => (
                            <li key={idx}>
                              <Text type="danger">
                                {typeof error === 'string' ? error : JSON.stringify(error)}
                              </Text>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              }
              showIcon
            />
          )}
        </Space>
      </Card>

      {/* Avisos */}
      <Alert
        type="warning"
        message="⚠️ Atenção"
        description={
          <ul>
            <li>Sempre faça backup dos dados antes de importar</li>
            <li>Valide os dados antes de realizar a importação</li>
            <li>Dados duplicados (mesmo CNPJ, CPF, etc) serão ignorados</li>
            <li>A importação não pode ser desfeita</li>
            <li>Siga a ordem: Tipos → Unidades → Modalidades → Equipe → Turmas → Alunos → Matrículas</li>
          </ul>
        }
        style={{ marginTop: 24 }}
      />

      <Alert
        type="info"
        message="💡 Dica"
        description="Para importar seus dados pela primeira vez, baixe os Excels, preencha-os com seus dados reais seguindo os exemplos, e importe na ordem correta."
        style={{ marginTop: 16 }}
      />

      {/* Loading Overlay */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <Card>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Processando...</p>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DataManagement;
